import React, { useState } from "react";
import {
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalCloseButton,
  Icon,
  ModalBody,
  Button,
  SimpleGrid,
  FormControl,
  FormLabel,
  Input,
} from "@chakra-ui/react";
import {
  FaEdit,
  FaCheckCircle,
  FaCalendarCheck,
  FaCalendarDay,
  FaCalendarAlt,
} from "react-icons/fa";
import { BASE_URL } from "../../../Globalapi/Globalapi";

const EditYear = ({ id }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [academicYear, setAcademicYear] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [updatedYear, setUpdatedYear] = useState({
    year_name: "",
    start_date: "",
    end_date: "",
    status: "active", // Default value
  });
  const schoolId = sessionStorage.getItem("school_id");
  const userId = sessionStorage.getItem("user_id");

  // Function to handle the modal opening
  const onOpen = async (id) => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/admin/academic_year/view_year.php?id=${id}`
      );
      const data = await response.json();

      if (data.success) {
        setAcademicYear(data.data);
        setUpdatedYear({
          year_name: data.data.year_name,
          start_date: data.data.start_date,
          end_date: data.data.end_date,
          status: data.data.status || "active", // Default to 'active' if not found
        });
        setError(null);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("Failed to fetch data");
    } finally {
      setIsLoading(false);
    }

    setIsOpen(true); // Show the modal
  };

  // Function to handle modal close
  const onClose = () => {
    setIsOpen(false);
    setAcademicYear(null);
    setError(null);
  };

  // Handle form input change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUpdatedYear((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Submit the updated data to the edit API
  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/admin/academic_year/edit_year.php`,
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            id, // Pass the ID of the academic year to be updated
            user_id: userId, // Assuming you have the user ID
            school_id: schoolId, // Assuming you have the school ID
            ...updatedYear, // Send the updated year data
          }),
        }
      );

      const data = await response.json();
      if (data.success) {
        setError(null);
        onClose(); // Close the modal after successful update
        alert("Updated successfully!");
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("Failed to update");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      <IconButton
        icon={<FaEdit />}
        size="sm"
        colorScheme="blue"
        onClick={() => onOpen(id)} // Pass the actual ID here
        mr={2}
      />

      <Modal
        isOpen={isOpen}
        onClose={onClose}
        size="lg"
        motionPreset="slideInBottom"
      >
        <ModalOverlay bg="blackAlpha.300" backdropFilter="blur(10px)" />
        <ModalContent
          borderRadius="lg"
          boxShadow="xl"
          bg="linear-gradient(135deg, #e0f7f7, #f0ffff)"
          color="gray.700"
          p={6}
          maxW="700px"
          maxH="550px"
          overflowY="auto"
        >
          <ModalHeader
            fontSize="xl"
            fontWeight="bold"
            textAlign="center"
            color="teal.600"
          >
            <Icon as={FaEdit} boxSize={5} mr={2} />
            Edit Academic Year
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody
            overflowY="auto"
            px={4}
            css={{
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb": {
                background: "rgba(173, 216, 230, 0.6)",
                borderRadius: "10px",
                backdropFilter: "blur(8px)",
              },
              "&::-webkit-scrollbar-thumb:hover": {
                background: "rgba(173, 216, 230, 0.9)",
              },
            }}
          >
            <SimpleGrid columns={2} spacing={4}>
              <FormControl mb={4}>
                <Icon as={FaCalendarAlt} mr={1} color="teal.500" />
                <FormLabel htmlFor="year_name">Year Name</FormLabel>
                <Input
                  placeholder="Name"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  id="year_name"
                  name="year_name"
                  value={updatedYear.year_name}
                  onChange={handleInputChange}
                />
              </FormControl>
              <FormControl mb={4}>
                <Icon as={FaCalendarDay} mr={1} color="teal.500" />
                <FormLabel htmlFor="start_date">Start Date</FormLabel>
                <Input
                  placeholder="Name"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  id="start_date"
                  name="start_date"
                  value={updatedYear.start_date}
                  onChange={handleInputChange}
                />
              </FormControl>
              <FormControl mb={4}>
                <Icon as={FaCalendarCheck} mr={1} color="teal.500" />
                <FormLabel htmlFor="end_date">End Date</FormLabel>
                <Input
                  placeholder="Name"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  id="end_date"
                  name="end_date"
                  value={updatedYear.end_date}
                  onChange={handleInputChange}
                />
              </FormControl>
              <FormControl mb={4}>
                <Icon as={FaCheckCircle} mr={1} color="teal.500" />
                <FormLabel htmlFor="status">Status</FormLabel>
                <Input
                  placeholder="Name"
                  bg="white"
                  _hover={{ borderColor: "teal.300" }}
                  id="status"
                  name="status"
                  value={updatedYear.status}
                  onChange={handleInputChange}
                />
              </FormControl>
            </SimpleGrid>

            <Flex justify="flex-end">
              <Button
                bgGradient="linear(to-r, teal.400, teal.600)"
                color="white"
                _hover={{ bgGradient: "linear(to-r, teal.500, teal.700)" }}
                onClick={handleSubmit}
              >
                Save
              </Button>
            </Flex>
          </ModalBody>
        </ModalContent>
      </Modal>
    </div>
  );
};

export default EditYear;
